/**
 * 「IXD 위치 검토」 — DSD 표의 행·열마다 값이 들어간 IXD 위치를 모으고, 사람이 확인할 만한
 * 줄(이름이 머리와 다르거나 IXD 여러 곳으로 갈라진 줄)을 「다음」 으로 차례로 짚어 주는 상태.
 *
 * 검토를 쉽게 하는 보조 기능이다. 골라 준 줄만 보면 끝나는 검증이 아니다 — 이름이 같은 줄,
 * IXD 에 없는 값, 글자 값은 이 목록에 없다. 화면 문구가 "모두 정상" 처럼 읽히지 않게 한다.
 *
 * 한 줄로 이동하면 그 줄의 DSD 칸과 IXD 칸을 함께 강조한다 — 이동만으로 비교가 끝나 있어야
 * 순회가 주 동선이 된다.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { DSDSubChunkInfo, DsdParsedCell } from "@/apis/compare/types";
import {
  buildTableCounterparts,
  counterpartFlag,
  type FactLabels,
  type HeaderCounterpart,
} from "@/utils/ixd-dsd-compare/header-counterpart";
import { logPositionCheck } from "@/utils/usage-log";

export interface FlaggedLine {
  /** `${blockKey}:${dlViewIds}:${axis}${index}` — 청크 안에서 줄 하나를 가리킨다 */
  key: string;
  counterpart: HeaderCounterpart;
  flag: "diff" | "split";
  /** 이 줄의 표가 DL 영역 여럿에 걸치는가 — 그러면 같은 머리에 영역별 줄이 따로 있다 */
  inMultiRegionTable: boolean;
}

export interface PositionCheckSummary {
  /** blockKey(`subIdx-blockIdx`) → 그 표의 행·열 대응 */
  counterpartsByBlock: Map<string, HeaderCounterpart[]>;
  /** 문서 순서의 확인할 줄 */
  flagged: FlaggedLine[];
  /** dsdId → 칸 글자 — 비교 카드의 "머리 이름" */
  textByDsdId: Map<string, string>;
}

/**
 * 화면의 표마다 대응을 만들고 확인할 줄을 모은다. 칸이 하나뿐인 표는 렌더러가 문단으로
 * 그리므로 머리가 없어 뺀다.
 */
export const summarizePositionCheck = (
  subChunks: readonly DSDSubChunkInfo[],
  factLabels: FactLabels,
  regionOf?: (dsdId: string) => string | null,
): PositionCheckSummary => {
  const counterpartsByBlock = new Map<string, HeaderCounterpart[]>();
  const flagged: FlaggedLine[] = [];
  const textByDsdId = new Map<string, string>();
  subChunks.forEach((subChunk, subIdx) =>
    subChunk.parsedContent?.forEach((block, blockIdx) => {
      if (block.blockType !== "table" || !block.cells) return;
      const origins = block.cells
        .flat()
        .filter((cell): cell is DsdParsedCell => !!cell?.isOrigin);
      if (origins.length <= 1) return;
      for (const cell of origins) textByDsdId.set(cell.dsdId, cell.text);
      const blockKey = `${subIdx}-${blockIdx}`;
      const counterparts = buildTableCounterparts(
        block.cells,
        factLabels,
        block.headerRowIndices,
        regionOf,
      );
      counterpartsByBlock.set(blockKey, counterparts);
      const inMultiRegionTable =
        new Set(
          counterparts.map((counterpart) => counterpart.dlViewIds.join(",")),
        ).size > 1;
      for (const counterpart of counterparts) {
        const flag = counterpartFlag(counterpart);
        if (flag)
          flagged.push({
            key: lineKey(blockKey, counterpart),
            counterpart,
            flag,
            inMultiRegionTable,
          });
      }
    }),
  );
  return { counterpartsByBlock, flagged, textByDsdId };
};

export const lineKey = (blockKey: string, counterpart: HeaderCounterpart) =>
  `${blockKey}:${counterpart.dlViewIds.map((id) => id ?? "-").join(",")}:${counterpart.axis}${counterpart.index}`;

const lineDsdIds = (counterpart: HeaderCounterpart): string[] => [
  ...counterpart.destinations.flatMap((d) => d.dsdIds),
  ...counterpart.unmatchedDsdIds,
];

const NEW_BADGE_KEY = "ixd-dsd-position-check:new-seen";

// 저장소가 막힌 환경(시크릿 창 등)에서는 NEW 배지가 다시 뜰 뿐 검토는 그대로 된다.
const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 저장 실패는 다음에 NEW 배지가 다시 뜨는 것으로 끝난다 */
  }
};
const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLElement && target.isContentEditable);

/**
 * 켜기 버튼에 다는 표시 — 그 버튼에 포커스가 남은 Enter 만 막는다. 카드의 「다음」·「이전」·
 * 도움말은 Tab 으로 가서 Enter 로 눌러야 한다.
 */
export const POSITION_REVIEW_TOGGLE_ATTR = "data-position-review-toggle";

/**
 * 모달이 떠 있으면 키를 가져오지 않는다 — 뒤 화면에서 검토가 켜지고, 켜진 뒤로는 모달의
 * Enter·방향키 전파까지 끊긴다. Radix Dialog 는 `aria-modal` 을 달고 Popover(검토 카드·
 * 도움말)는 달지 않아 둘을 가른다.
 *
 * 검토 중에 모달이 열리는 경로는 여기서 막지 않는다 — 입력 모드 소유로 풀 일이다
 * (docs/ixd-dsd-compare-position-review-refactor.md 2.1).
 */
const isModalOpen = () =>
  !!document.querySelector('[role="dialog"][aria-modal="true"]');

interface Params {
  subChunks: DSDSubChunkInfo[];
  /** null 이면 기능을 끈다(편집 모드 등) */
  factLabels: FactLabels | null | undefined;
  /** 서버가 DSD 칸에 배정한 DL 영역 — 영역마다 행·열 대응을 따로 만든다 */
  dlViewIdByDsdId?: ReadonlyMap<string, string> | null;
  /** 그 줄의 머리 칸을 DSD 미리보기에서 보이게 한다 */
  scrollToCell?: (dsdId: string) => void;
  /**
   * 줄로 이동할 때 그 줄의 DSD 칸을 IXD 쪽에서도 강조하도록 넘긴다. `anchorDsdId` 는 그 줄의
   * 머리 칸 — IXD 를 이 높이에 맞춘다. 빈 목록이면 강조를 걷는다.
   */
  onShowLine?: (dsdIds: string[], anchorDsdId?: string) => void;
}

export const usePositionCheck = ({
  subChunks,
  factLabels,
  dlViewIdByDsdId,
  scrollToCell,
  onShowLine,
}: Params) => {
  const summary = useMemo(
    () =>
      factLabels
        ? summarizePositionCheck(
            subChunks,
            factLabels,
            (dsdId) => dlViewIdByDsdId?.get(dsdId) ?? null,
          )
        : null,
    [subChunks, factLabels, dlViewIdByDsdId],
  );

  /** 도구를 켰는가 — 사용자가 「위치 검토 시작」 을 눌러야 켜진다 */
  const [active, setActive] = useState(false);
  /** 지금 보는 확인할 줄의 순번. -1 은 아직 줄 위가 아님, `flagged.length` 면 끝까지 본 상태 */
  const [cursor, setCursor] = useState(-1);
  const [highlighted, setHighlighted] = useState<Set<string> | null>(null);
  const [showNewBadge, setShowNewBadge] = useState(
    () => readStorage(NEW_BADGE_KEY) !== "1",
  );

  // 청크를 옮겨도 켠 상태는 유지한다 — 보고서를 이어서 검토하는 흐름이다. 줄 위치만 새로 잡는다.
  const [prevSummary, setPrevSummary] = useState(summary);
  if (prevSummary !== summary) {
    setPrevSummary(summary);
    setCursor(-1);
    setHighlighted(null);
  }

  // 편집 모드처럼 라벨 사전이 없으면 켜 둔 상태여도 꺼진 것으로 본다.
  const isOn = active && !!summary;

  // 그리고 켜 둔 상태 자체를 접는다 — 안 접으면 편집을 마치고 돌아올 때 아무것도 누르지
  // 않았는데 검토가 다시 켜져, 방향키를 가져가고 다른 키의 전파를 끊는다. IXD 쪽 강조도
  // 이 경로에서 함께 걷는다(렌더 중 상태 보정은 콜백을 부를 자리가 아니다).
  // 사람이 끈 것이 아니므로 로그는 남기지 않는다.
  useEffect(() => {
    if (summary || !active) return;
    setActive(false);
    onShowLine?.([]);
  }, [summary, active, onShowLine]);

  const flagged = useMemo(() => summary?.flagged ?? [], [summary]);
  const current = isOn ? (flagged[cursor] ?? null) : null;
  const finished = isOn && flagged.length > 0 && cursor >= flagged.length;

  const touchBar = useCallback(() => {
    if (!showNewBadge) return;
    writeStorage(NEW_BADGE_KEY, "1");
    setShowNewBadge(false);
  }, [showNewBadge]);

  const moveTo = useCallback(
    (index: number) => {
      touchBar();
      setActive(true);
      setCursor(index);
      const line = flagged[index];
      if (!line) {
        setHighlighted(null);
        onShowLine?.([]);
        return;
      }
      const dsdIds = lineDsdIds(line.counterpart);
      setHighlighted(new Set(dsdIds));
      // DSD 를 먼저 움직인다 — IXD 는 DSD 줄이 멈춘 높이에 맞춘다.
      scrollToCell?.(line.counterpart.anchorDsdId);
      onShowLine?.(dsdIds, line.counterpart.anchorDsdId);
    },
    [flagged, onShowLine, scrollToCell, touchBar],
  );

  /** 도구를 켜고 첫 줄로 간다. 확인할 줄이 없어도 켜서 "없음" 을 보여 준다 */
  const activate = useCallback(() => {
    if (!summary) return;
    logPositionCheck("검토 시작");
    if (flagged.length) moveTo(0);
    else {
      touchBar();
      setActive(true);
    }
  }, [summary, flagged.length, moveTo, touchBar]);

  /** 도구를 끈다 */
  const deactivate = useCallback(() => {
    logPositionCheck("검토 종료");
    setActive(false);
    setCursor(-1);
    setHighlighted(null);
    onShowLine?.([]);
  }, [onShowLine]);

  const next = useCallback(() => {
    if (!isOn) {
      activate();
      return;
    }
    if (!flagged.length) return;
    logPositionCheck("다음");
    // 마지막 줄을 넘기면 끝 상태 카드를 보여 준다. 검토는 켜 둔 채라 이전으로 돌아갈 수 있다.
    moveTo(Math.min(cursor + 1, flagged.length));
  }, [isOn, activate, flagged.length, cursor, moveTo]);

  const prev = useCallback(() => {
    if (!isOn || !flagged.length) return;
    logPositionCheck("이전");
    moveTo(Math.max(cursor - 1, 0));
  }, [isOn, flagged.length, cursor, moveTo]);

  /** 점·머리 칸을 눌러 그 줄로 바로 간다 — 순회의 보조 경로 */
  const jumpTo = useCallback(
    (key: string) => {
      const index = flagged.findIndex((line) => line.key === key);
      if (index < 0) return;
      logPositionCheck("줄로 이동");
      moveTo(index);
    },
    [flagged, moveTo],
  );

  const clearHighlight = useCallback(() => setHighlighted(null), []);

  // I 켜기 · Esc 끄기 · → 다음 · ← 이전. 물리 키(code)로 봐서
  // 한글 입력 중에도 같은 키다. 좌우 방향키는 검토 중에만 가져온다 — 꺼져 있으면 셀 이동 그대로다.
  // 검토 중에는 window 캡처 단계에서 먼저 받아 다른 키는 화면의 기존 단축키로 넘기지 않는다 —
  // 셀 이동·선택이 검토 위치와 엇갈리지 않게. Esc 는 열린 창을 닫아야 하니 넘긴다. 기본 동작은
  // 막지 않아 키로 스크롤하는 것은 그대로 된다.
  // Esc 는 검토를 끄면서도 넘긴다 — 열린 창이 있으면 함께 닫힌다.
  useEffect(() => {
    if (!summary) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const plain =
        !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      // 켜기는 I 하나다 — 괄호는 다른 화면에서도 흔히 눌려 뜻하지 않게 검토가 켜진다.
      if (!isOn) {
        if (plain && event.code === "KeyI" && !isModalOpen()) {
          event.preventDefault();
          event.stopPropagation();
          activate();
        }
        return;
      }
      if (event.key === "Escape") {
        deactivate();
        return;
      }
      if (plain && event.code === "ArrowRight") {
        event.preventDefault();
        next();
      } else if (plain && event.code === "ArrowLeft") {
        event.preventDefault();
        prev();
      } else if (
        event.key === "Enter" &&
        event.target instanceof Element &&
        event.target.closest(`[${POSITION_REVIEW_TOGGLE_ATTR}]`)
      ) {
        // 클릭으로 켜면 포커스가 켜기 버튼에 남아 Enter 가 그 버튼을 다시 눌러 검토를 꺼 버린다.
        // 그 버튼 하나만 막는다 — 일괄로 막으면 카드의 「다음」·「이전」·도움말을 Tab 으로
        // 짚어도 Enter 가 듣지 않는다.
        event.preventDefault();
      }
      event.stopPropagation();
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [summary, isOn, next, prev, activate, deactivate]);

  return {
    summary,
    active: isOn,
    cursor,
    current,
    finished,
    highlighted,
    showNewBadge,
    activate,
    deactivate,
    next,
    prev,
    jumpTo,
    clearHighlight,
  };
};
